import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  BuildingStorefrontIcon,
  UserIcon,
  TruckIcon,
  BriefcaseIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ReceiptPercentIcon,
  CreditCardIcon,
  CalendarIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { customerService } from '../../services/customerService'
import toast from 'react-hot-toast'

interface CustomerFormData {
  company_name: string
  first_name: string
  last_name: string
  email: string
  phone: string
  address: string
  city: string
  country: string
  tax_number: string
  group_id: string
  credit_limit: string
  notes: string
}

const getGroupIcon = (tier: string) => {
  switch (tier) {
    case 'wholesale': return BuildingStorefrontIcon
    case 'corporate': return BriefcaseIcon
    case 'consignment': return TruckIcon
    default: return UserIcon
  }
}

const getGroupColor = (tier: string) => {
  switch (tier) {
    case 'wholesale': return 'bg-blue-100 text-blue-800'
    case 'corporate': return 'bg-purple-100 text-purple-800'
    case 'consignment': return 'bg-orange-100 text-orange-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

export const CustomerDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { data: customerData, isLoading: isLoadingCustomer } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customerService.getById(Number(id)),
  })

  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['customer-groups'],
    queryFn: () => customerService.getGroups(),
  })

  const { data: purchaseHistoryData, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['customer-purchases', id],
    queryFn: () => customerService.getPurchaseHistory(Number(id)),
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CustomerFormData>) => customerService.update(Number(id), data),
    onSuccess: () => {
      toast.success('Customer updated successfully')
      queryClient.invalidateQueries({ queryKey: ['customer', id] })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      setIsEditing(false)
    },
    onError: () => {
      toast.error('Failed to update customer')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => customerService.delete(Number(id)),
    onSuccess: () => {
      toast.success('Customer deleted successfully')
      navigate('/customers')
    },
    onError: () => {
      toast.error('Failed to delete customer')
    },
  })

  if (isLoadingCustomer || isLoadingGroups) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  const customer = customerData?.data?.data
  const groups = groupsData?.data?.data || []

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Customer not found</p>
        <Link to="/customers" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Customers
        </Link>
      </div>
    )
  }

  const purchaseHistory = purchaseHistoryData?.data?.data?.items || []
  const totalPurchases = customer.total_purchases || 0
  const totalOrders = customer.total_orders || 0

  const customerName = customer.company_name || 
    `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 
    'Walk-in Customer'

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data: Partial<CustomerFormData> = {
      company_name: formData.get('company_name') as string,
      first_name: formData.get('first_name') as string,
      last_name: formData.get('last_name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      address: formData.get('address') as string,
      city: formData.get('city') as string,
      country: formData.get('country') as string,
      tax_number: formData.get('tax_number') as string,
      group_id: formData.get('group_id') as string,
      credit_limit: formData.get('credit_limit') as string,
      notes: formData.get('notes') as string,
    }
    updateMutation.mutate(data)
  }

  const GroupIcon = getGroupIcon(customer.customer_groups?.price_tier || 'retail')
  const groupColor = getGroupColor(customer.customer_groups?.price_tier || 'retail')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/customers"
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{customerName}</h1>
            <p className="text-sm text-gray-500">{customer.customer_code}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <PencilIcon className="w-4 h-4" />
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <TrashIcon className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Customer Group Badge */}
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${groupColor}`}>
        <GroupIcon className="w-5 h-5" />
        <span className="font-medium">{customer.customer_groups?.group_name || 'Retail'}</span>
        {customer.customer_groups?.price_tier && (
          <span className="text-xs opacity-75">({customer.customer_groups.price_tier})</span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Edit Form */}
          {isEditing ? (
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Edit Customer</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Company Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    defaultValue={customer.company_name || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g. ABC Photography Ltd"
                  />
                </div>

                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    defaultValue={customer.first_name || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    defaultValue={customer.last_name || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={customer.email || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    defaultValue={customer.phone || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Customer Group */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Group *
                  </label>
                  <select
                    name="group_id"
                    defaultValue={customer.group_id || ''}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select Group...</option>
                    {groups.map((group: any) => (
                      <option key={group.group_id} value={group.group_id}>
                        {group.group_name} ({group.price_tier})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Credit Limit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credit Limit (KSh)
                  </label>
                  <input
                    type="number"
                    name="credit_limit"
                    defaultValue={customer.credit_limit ?? 0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="0.00"
                  />
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    name="address"
                    defaultValue={customer.address || ''}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    defaultValue={customer.city || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    defaultValue={customer.country || 'Kenya'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Tax Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Number (KRA PIN)
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    defaultValue={customer.tax_number || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="P000123456G"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    defaultValue={customer.notes || ''}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Additional notes about this customer..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {updateMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <>
              {/* Contact Info */}
              <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {customer.email && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <EnvelopeIcon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{customer.email}</p>
                      </div>
                    </div>
                  )}
                  {customer.phone && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <PhoneIcon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="font-medium">{customer.phone}</p>
                      </div>
                    </div>
                  )}
                  {customer.address && (
                    <div className="flex items-start gap-3 md:col-span-2">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <MapPinIcon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Address</p>
                        <p className="font-medium">{customer.address}</p>
                        <p className="text-gray-600">{customer.city}, {customer.country}</p>
                      </div>
                    </div>
                  )}
                  {customer.tax_number && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <ReceiptPercentIcon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">KRA PIN</p>
                        <p className="font-medium">{customer.tax_number}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Purchase History */}
              <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Purchase History</h2>
                  <span className="text-sm text-gray-500">{totalOrders} orders</span>
                </div>
                
                {isLoadingHistory ? (
                  <div className="p-8 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                  </div>
                ) : purchaseHistory.length === 0 ? (
                  <div className="p-8 text-center">
                    <ShoppingBagIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No purchase history yet</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {purchaseHistory.slice(0, 5).map((sale: any) => (
                        <tr key={sale.sale_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <Link 
                              to={`/sales/${sale.sale_id}`}
                              className="font-medium text-primary-600 hover:underline"
                            >
                              {sale.sale_number}
                            </Link>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {new Date(sale.sale_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-right font-medium">
                            KSh {Number(sale.total_amount).toLocaleString()}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              sale.payment_status === 'paid' 
                                ? 'bg-green-100 text-green-800'
                                : sale.payment_status === 'partial'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {sale.payment_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                
                {purchaseHistory.length > 5 && (
                  <div className="px-6 py-3 border-t border-gray-200">
                    <button className="text-sm text-primary-600 hover:underline">
                      View all {totalOrders} orders
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Group Info */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Group Details</h2>
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${groupColor}`}>
                <div className="flex items-center gap-3">
                  <GroupIcon className="w-6 h-6" />
                  <div>
                    <p className="font-semibold">{customer.customer_groups?.group_name || 'Retail'}</p>
                    <p className="text-sm opacity-75">{customer.customer_groups?.price_tier || 'retail'} pricing</p>
                  </div>
                </div>
              </div>
              
              {customer.customer_groups?.payment_terms_days > 0 && (
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Payment Terms</p>
                    <p className="font-medium">Net {customer.customer_groups.payment_terms_days} days</p>
                  </div>
                </div>
              )}
              
              {customer.customer_groups?.discount_percent > 0 && (
                <div className="flex items-center gap-3">
                  <ReceiptPercentIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Group Discount</p>
                    <p className="font-medium">{customer.customer_groups.discount_percent}%</p>
                  </div>
                </div>
              )}
              
              {customer.credit_limit > 0 && (
                <div className="flex items-center gap-3">
                  <CreditCardIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Credit Limit</p>
                    <p className="font-medium">KSh {Number(customer.credit_limit).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg p-6 text-white">
            <h2 className="font-semibold mb-4">Customer Stats</h2>
            <div className="space-y-4">
              <div>
                <p className="text-primary-100 text-sm">Total Purchases</p>
                <p className="text-3xl font-bold">KSh {Number(totalPurchases).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-primary-100 text-sm">Total Orders</p>
                <p className="text-2xl font-bold">{totalOrders}</p>
              </div>
              {customer.current_balance > 0 && (
                <div className="pt-4 border-t border-primary-400">
                  <p className="text-primary-100 text-sm">Current Balance</p>
                  <p className="text-xl font-bold text-yellow-300">
                    KSh {Number(customer.current_balance).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Account Info</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Customer Since</span>
                <span className="text-gray-900">
                  {new Date(customer.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated</span>
                <span className="text-gray-900">
                  {new Date(customer.updated_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {customer.notes && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
              <h2 className="font-semibold text-yellow-900 mb-2">Notes</h2>
              <p className="text-yellow-800 text-sm">{customer.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Customer</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{customerName}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
